import PropTypes from "prop-types";
import { useState } from "react";
import NFTTableOptionsMenu from "./NFTTableOptionsMenu";
import useModal from "../../../../hooks/useModal";
import NFTCreateModal from "./NFTCreateModal";
import JSZip from "jszip";
import QRCode from 'qrcode';
import toast from "react-hot-toast";
import { Button } from "@oc-labs/wt-storybook";
import useCompany from "../../../../hooks/useCompany";
import useRwas from "../../../../hooks/useRwas";

export default function NFTTable({ nfts, rwaId, reloadNfts }) {
  const { activeCompany: company } = useCompany();
  const [selectedNfts, setSelectedNfts] = useState([]);
  const [isDeletingNfts, setIsDeletingNfts] = useState(false);
  const createModal = useModal("nft-create-modal");
  const { deleteRwaNft } = useRwas();

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedNfts(nfts.map((nft) => nft.id));
    } else {
      setSelectedNfts([]);
    }
  };

  const generateSvg = async (nft) => {
    const qrBuffer = await QRCode.toDataURL(nft?.url, {
      type: "image/png",
      color: {
        dark: "#FFF", // Blue dots
        light: "#0000", // Transparent background
      },
    });
    return qrBuffer;
  };

  const handleZipFiles = async (files) => {
    const zip = new JSZip();

    files.forEach((file) => {
      zip.file(file.name, file.content, { binary: true });
    });
    const content = await zip.generateAsync({ type: "blob" });
    const url = URL.createObjectURL(content);
    const link = document.createElement("a");
    link.href = url;
    link.download = "nfts.zip";
    link.click();
    URL.revokeObjectURL(url);
    toast.success("NFTs downloaded successfully");
  };

  const handleExport = async () => {
    let files = [];
    for (const nft of selectedNfts.map((id) =>
      nfts.find((nft) => nft.id === id)
    )) {
      const qr = await generateSvg(nft);
      const blob = await fetch(qr).then((res) => res.blob());
      const file = new File([blob], `${nft.id}.png`, { type: "image/png" });
      files.push({ name: `${nft.id}.png`, content: file });
    }
    await handleZipFiles(files);
    setSelectedNfts([]);
  };

  const handleDeleteNFTs = async () => {
    if (selectedNfts.length === 0) {
      toast.error("No NFTs selected for deletion");
      return;
    }
    if (isDeletingNfts) return;

    setIsDeletingNfts(true);
    try {
      for (const nftId of selectedNfts) {
        await deleteRwaNft(company.id, rwaId, nftId);
      }
      toast.success("NFTs deleted successfully");
      setSelectedNfts([]);
      reloadNfts();
    } catch (error) {
      console.error("Error deleting NFTs:", error);
      toast.error("Failed to delete NFTs");
    } finally {
      setIsDeletingNfts(false);
    }
  }

  const filteredNfts = nfts.sort((a, b) => {
    return new Number(a.serial) - new Number(b.serial);
  });

  const handleSelect = (nftId, e) => {
    if (e.target.id === "nft-options-button") return;
    if (selectedNfts.includes(nftId)) {
      setSelectedNfts(selectedNfts.filter((id) => id !== nftId));
    } else {
      setSelectedNfts([...selectedNfts, nftId]);
    }
  };

  return (
    <>
      <NFTCreateModal reloadNfts={reloadNfts} rwaId={rwaId} />

      {isDeletingNfts && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40">
          <div className="bg-white rounded-2xl p-8 shadow-xl flex flex-col items-center gap-4">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-gray-900" />
            <p className="text-base font-medium text-gray-800">Your NFTs are being deleted…</p>
            <p className="text-sm text-gray-500">This may take a moment. Please wait.</p>
          </div>
        </div>
      )}
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-semibold">NFT</h1>
        <div className="ml-auto flex space-x-4">
          <button
            onClick={() => createModal.open()}
            className="px-6 py-2 bg-gray-100 rounded-full text-sm font-semibold cursor-pointer mr-4"
          >
            Create
          </button>

          <Button
            onClick={handleDeleteNFTs}
            type="primary"
            disabled={selectedNfts.length === 0}
            className="px-6 py-2 bg-gray-100 rounded-full text-sm font-semibold cursor-pointer mr-4"
          >
            Delete
          </Button>

          <button
            onClick={handleExport}
            className={`px-6 py-2 rounded-full text-sm font-semibold mr-4 ${selectedNfts.length === 0
                ? "bg-gray-200 opacity-20"
                : "bg-indigo-500 cursor-pointer"
              }`}
            disabled={selectedNfts.length === 0}
          >
            Export
          </button>
        </div>
      </div>

      <div>
        <table className="w-full text-left border-collapse table-fixed">
          <thead>
            <tr>
              <th className="py-2 w-[4%]">
                <input
                  type="checkbox"
                  className="h-4 w-4 text-gray-600 border-gray-300 rounded focus:ring-gray-500"
                  onChange={handleSelectAll}
                  checked={selectedNfts.length === nfts.length}
                />
              </th>
              <th className="px-2 py-2 uppercase text-xs font-semibold w-[13%]">
                <span className="bg-gray-100 rounded-lg px-2 py-1">ID</span>
              </th>
              <th className="px-2 py-2 uppercase text-xs font-semibold w-[9%]">
                <span className="bg-gray-100 rounded-lg px-2 py-1">Serial</span>
              </th>
              <th className="px-2 py-2 uppercase text-xs font-semibold w-[22%]">
                <span className="bg-gray-100 rounded-lg px-2 py-1">SECRET</span>
              </th>
              <th className="px-2 py-2 uppercase text-xs font-semibold w-[16%]">
                <span className="bg-gray-100 rounded-lg px-2 py-1">
                  MINTED AT
                </span>
              </th>
              <th className="px-2 py-2 uppercase text-xs font-semibold w-[16%]">
                <span className="bg-gray-100 rounded-lg px-2 py-1">
                  MINTED BY
                </span>
              </th>
              <th className="px-2 py-2 uppercase text-xs font-semibold w-[20%]">
                <span className="bg-gray-100 rounded-lg px-2 py-1">
                  ACTIONS
                </span>
              </th>
            </tr>
          </thead>

          <tbody>
            {filteredNfts?.map((nft) => (
              <tr key={nft.id} className={`hover:bg-gray-200 cursor-pointer ${selectedNfts.includes(nft.id) ? "bg-gray-100" : ""}`} onClick={(e) => handleSelect(nft.id, e)}>
                <td className="py-3">
                  <input
                    type="checkbox"
                    className="h-4 w-4 text-gray-600 border-gray-300 rounded focus:ring-gray-500"
                    value={nft.id}
                    checked={selectedNfts.includes(nft.id)}
                    onChange={() => handleSelect(nft.id)}
                  />
                </td>
                <td className="px-2 py-3 text-sm truncate">{nft.id.slice(0, 8)}...</td>
                <td className="px-2 py-3 text-sm truncate">{nft.serial || 'Non assign'}</td>
                <td className="px-2 py-3 text-sm truncate">{nft.secret}</td>
                <td className="px-2 py-3 text-sm truncate">{nft.mintedAt}</td>
                <td className="px-2 py-3 text-sm truncate">{nft.mintedBy}</td>
                <td className="px-2 py-3 relative">
                  <NFTTableOptionsMenu nft={nft} />
                </td>
              </tr>
            ))}

            {nfts.length === 0 && (
              <tr>
                <td className="px-4 py-4 text-gray-500 italic" colSpan={7}>
                  No NFTs created yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}

NFTTable.propTypes = {
  nfts: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      secret: PropTypes.string.isRequired,
      mintedAt: PropTypes.string.isRequired,
      mintedBy: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
    })
  ).isRequired,
};
